import type { Athlete, Company, MatchResult, MatchWeights } from '../types/domain'

const normalize = (value: number, max: number) => Math.max(0, Math.min(1, value / max))
const overlap = (a: string[], b: string[]) => a.filter((item) => b.map((v) => v.toLowerCase()).includes(item.toLowerCase()))

export function normalizeWeights(weights: MatchWeights): MatchWeights {
  const total = Object.values(weights).reduce((sum, value) => sum + value, 0) || 1
  const normalized = { ...weights }
  for (const key of Object.keys(weights) as Array<keyof MatchWeights>) {
    normalized[key] = (weights[key] / total) * 100
  }
  return normalized
}

export function calculateMatch(athlete: Athlete, company: Company, rawWeights: MatchWeights): MatchResult {
  const weights = normalizeWeights(rawWeights)
  const sameRegion = athlete.region === company.region
  const sameCity = athlete.city === company.city
  const inRadius = (athlete.distanceKm ?? 999) <= company.radiusKm
  const territoryFit = sameCity ? 1 : sameRegion ? 0.95 : inRadius ? 0.7 : 0.25

  const budgetMid = (company.budgetMin + company.budgetMax) / 2
  const budgetGap = Math.abs(athlete.budgetNeeded - budgetMid)
  const budgetFit = athlete.budgetNeeded <= company.budgetMax
    ? athlete.budgetNeeded >= company.budgetMin ? 1 : 0.82
    : Math.max(0.15, 1 - budgetGap / Math.max(company.budgetMax, athlete.budgetNeeded))

  const commonValues = overlap(athlete.values, company.values)
  const commonCauses = overlap(athlete.causes, company.causes)
  const valuesFit = Math.min(1, commonValues.length * 0.28 + commonCauses.length * 0.22 + 0.18)

  const objectiveKeywords = [...company.objectives, ...company.causes].join(' ').toLowerCase()
  const objectiveSignals = [
    athlete.values.some((v) => objectiveKeywords.includes(v.toLowerCase())),
    athlete.contentTypes.some((v) => company.contentNeeds.map((x) => x.toLowerCase()).some((x) => v.toLowerCase().includes(x) || x.includes(v.toLowerCase()))),
    athlete.audienceZone.toLowerCase().includes(company.city.toLowerCase()) || sameRegion,
  ].filter(Boolean).length
  const objectivesFit = 0.35 + objectiveSignals * 0.2

  const disciplineFit = company.sports.some((sport) => sport.toLowerCase() === athlete.discipline.toLowerCase()) ? 1 : 0.45
  const audienceFit = Math.max(0.35, Math.min(1, normalize(athlete.audience, 25000) * 0.75 + normalize(athlete.engagementRate, 10) * 0.25))
  const commonContent = athlete.contentTypes.filter((item) => company.contentNeeds.some((need) => item.toLowerCase().includes(need.toLowerCase().split(' ')[0]) || need.toLowerCase().includes(item.toLowerCase().split(' ')[0])))
  const contentFit = commonContent.length ? Math.min(1, 0.55 + commonContent.length * 0.18) : 0.38
  const calendarFit = athlete.availability.toLowerCase().includes('disponible') ? 0.9 : 0.65
  const verificationFit = athlete.verified === 'athlink_verified' ? 1 : athlete.verified === 'sport_verified' ? 0.85 : athlete.verified === 'identity_verified' ? 0.7 : 0.5

  const criteria: Record<keyof MatchWeights, number> = {
    territory: territoryFit,
    budget: budgetFit,
    values: valuesFit,
    objectives: objectivesFit,
    discipline: disciplineFit,
    audience: audienceFit,
    content: contentFit,
    calendar: calendarFit,
    verification: verificationFit,
  }

  const score = Math.round(Object.entries(criteria).reduce((sum, [key, fit]) => sum + fit * weights[key as keyof MatchWeights], 0))
  const explanations: string[] = []
  if (sameCity) explanations.push(`Même ville : ${athlete.city}`)
  else if (sameRegion) explanations.push(`Même région : ${athlete.region}`)
  else if (inRadius) explanations.push(`Profil dans votre rayon de ${company.radiusKm} km`)
  if (budgetFit >= 0.9) explanations.push('Budget compatible avec votre fourchette')
  if (commonValues.length) explanations.push(`${commonValues.length} valeur${commonValues.length > 1 ? 's' : ''} commune${commonValues.length > 1 ? 's' : ''} : ${commonValues.slice(0, 3).join(', ')}`)
  if (commonCauses.length) explanations.push(`Engagement partagé : ${commonCauses[0]}`)
  if (disciplineFit === 1) explanations.push(`${athlete.discipline} fait partie de vos sports recherchés`)
  if (contentFit > 0.7) explanations.push('Contreparties adaptées à vos besoins de contenu')
  if (audienceFit > 0.75) explanations.push('Audience engagée et pertinente pour votre campagne')
  if (verificationFit >= 0.85) explanations.push('Parcours sportif vérifié')

  return {
    athleteId: athlete.id,
    companyId: company.id,
    score,
    level: score >= 80 ? 'excellent' : score >= 62 ? 'good' : 'explore',
    explanations: explanations.slice(0, 6),
    breakdown: Object.fromEntries(Object.entries(criteria).map(([key, fit]) => [key, Math.round(fit * weights[key as keyof MatchWeights])])) as Record<keyof MatchWeights, number>,
  }
}

export function rankAthletes(athletes: Athlete[], company: Company, weights: MatchWeights) {
  return athletes
    .map((athlete) => ({ athlete, match: calculateMatch(athlete, company, weights) }))
    .sort((a, b) => b.match.score - a.match.score)
}
